/**
 * Development-only logger that automatically strips logs in production
 * Use this instead of console.log throughout the application
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * `metric` is an always-on telemetry channel that sits deliberately OUTSIDE the
 * debug < info < warn < error severity ladder — it carries operational data
 * (token counts, cache hit/miss), not a severity. It emits in production like
 * warn/error, but routes to console.log so the warn/error failure channel
 * (#518) stays clean enough to alert on.
 */
type EmittedLevel = LogLevel | 'metric';

interface LoggerOptions {
  prefix?: string;
  enabled?: boolean;
  minLevel?: LogLevel;
}

// Log level hierarchy (higher number = higher priority)
const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
};

// Default minimum log level for development (can be overridden)
const DEFAULT_MIN_LEVEL: LogLevel = process.env.LOG_LEVEL as LogLevel || 'info';

class DevLogger {
  private prefix: string;
  private enabled: boolean;
  private minLevel: LogLevel;

  constructor(options: LoggerOptions = {}) {
    this.prefix = options.prefix || '';
    this.enabled = options.enabled !== false && process.env.NODE_ENV === 'development';
    this.minLevel = options.minLevel || DEFAULT_MIN_LEVEL;
  }

  private formatMessage(level: EmittedLevel, message: string, ...args: any[]): void {
    // warn/error always emit, including in production, so real failures
    // (e.g. dropped invoice emails) are never silently swallowed. `metric` is
    // always-on too — it is production telemetry whose whole purpose is to be
    // readable in Vercel runtime logs. debug/info stay gated behind `enabled`
    // (dev-only) and the minLevel filter.
    // NOTE: written as an inline narrowing check rather than an `isAlwaysOn`
    // boolean so TypeScript narrows `level` to 'debug' | 'info' for the
    // LOG_LEVELS lookup below ('metric' has no severity rank by design).
    if (level !== 'warn' && level !== 'error' && level !== 'metric') {
      if (!this.enabled) return;

      // Filter out logs below minimum level
      if (LOG_LEVELS[level] < LOG_LEVELS[this.minLevel]) return;
    }

    const timestamp = new Date().toISOString();
    const prefixStr = this.prefix ? `[${this.prefix}]` : '';
    const levelStr = `[${level.toUpperCase()}]`;

    const consoleMethod = level === 'error' ? console.error : 
                         level === 'warn' ? console.warn : 
                         console.log;

    consoleMethod(`${timestamp} ${levelStr} ${prefixStr} ${message}`, ...args);
  }

  debug(message: string, ...args: any[]): void {
    this.formatMessage('debug', message, ...args);
  }

  info(message: string, ...args: any[]): void {
    this.formatMessage('info', message, ...args);
  }

  warn(message: string, ...args: any[]): void {
    this.formatMessage('warn', message, ...args);
  }

  error(message: string, ...args: any[]): void {
    this.formatMessage('error', message, ...args);
  }

  /**
   * Production-safe operational telemetry for SERVER-SIDE counters. Always
   * emits via console.log, so numbers like Anthropic cache read/write tokens
   * reach Vercel runtime logs where they can actually be read.
   *
   * "Always" is literal: like warn/error, this bypasses BOTH the dev-only
   * `enabled` gate AND the `minLevel` filter — including an explicit
   * `createLogger(prefix, { enabled: false })`. There is no way to silence a
   * metric call short of deleting it, so only add ones you want permanently on.
   *
   * SERVER-SIDE ONLY. This module is imported by ~2,400 files including client
   * components; a `logger.metric` in client code would print in a customer's
   * browser console. Keep metric calls in API routes, cron jobs, and other
   * server paths.
   *
   * Use for numbers you need to observe in production. Do NOT use for failures
   * — that is what warn/error are for, and keeping them separate is what lets
   * the failure channel stay alertable.
   */
  metric(message: string, ...args: any[]): void {
    this.formatMessage('metric', message, ...args);
  }

  // Group functionality for complex debugging
  group(label: string): void {
    if (!this.enabled) return;
    console.group(`[${this.prefix}] ${label}`);
  }

  groupEnd(): void {
    if (!this.enabled) return;
    console.groupEnd();
  }

  // Table for structured data
  table(data: any): void {
    if (!this.enabled) return;
    console.table(data);
  }

  // Performance timing
  time(label: string): void {
    if (!this.enabled) return;
    console.time(`[${this.prefix}] ${label}`);
  }

  timeEnd(label: string): void {
    if (!this.enabled) return;
    console.timeEnd(`[${this.prefix}] ${label}`);
  }
}

// Factory function to create loggers with specific prefixes
export const createLogger = (prefix: string, options?: Omit<LoggerOptions, 'prefix'>): DevLogger => {
  return new DevLogger({ ...options, prefix });
};

// Helper to create debug-level loggers for detailed debugging
export const createDebugLogger = (prefix: string): DevLogger => {
  return new DevLogger({ prefix, minLevel: 'debug' });
};

// Pre-configured loggers for common areas
export const authLogger = createLogger('Auth');
export const formLogger = createLogger('Form');
export const apiLogger = createLogger('API');
export const routeLogger = createLogger('Route');
export const scheduleLogger = createLogger('Schedule');

// Default logger for general use
const defaultLogger = new DevLogger();
export default defaultLogger;

// Development helper to change log level at runtime
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).setLogLevel = (level: LogLevel) => {
    defaultLogger.info(`🔧 Setting log level to: ${level.toUpperCase()}`);
    defaultLogger.info(`💡 To change log level, set LOG_LEVEL=${level} in your environment and refresh`);
    defaultLogger.info(`   Example: LOG_LEVEL=debug npm run dev`);
  };
  
  defaultLogger.info(`🎯 DevLogger active (level: ${DEFAULT_MIN_LEVEL.toUpperCase()}). Use window.setLogLevel("info") for more detail`);
}
